import { useEffect } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { UtilsService, ApiError } from "../../gen/clients/llts";

interface PathParams {
  lltsBranchId?: string;
}

/**
 * Component that validates the lltsBranchId parameter in the URL.
 * If the branch ID is invalid (404 or other error), it redirects to the same page without the branch context.
 * This component should be rendered at the top level of the application to ensure branch validation
 * happens on every route.
 */
export const BranchValidator: React.FC = () => {
  const { lltsBranchId } = useParams<PathParams>();
  const location = useLocation();
  const history = useHistory();

  const { error } = useQuery(["getLltsBranch", lltsBranchId], {
    enabled: !!lltsBranchId,
    queryFn: () => UtilsService.getBranch({ branchId: lltsBranchId || "" }),
    retry: false // Don't retry on error - we want to redirect immediately
  });

  // Handle invalid branch ID by redirecting to the same path without the branch context
  useEffect(() => {
    if (error && lltsBranchId) {
      // Check if it's a 404 or any other error indicating invalid branch
      const isInvalidBranch = error instanceof ApiError && (error.status === 404 || error.status >= 400);

      if (isInvalidBranch) {
        // Remove the lltsBranchId from the current path
        const pathWithoutBranch = location.pathname.replace(`/${lltsBranchId}`, "");
        const newPath = pathWithoutBranch || "/";

        // Preserve search params and hash
        const fullPath = `${newPath}${location.search}${location.hash}`;

        // Replace the current history entry to avoid back button issues
        history.replace(fullPath);
      }
    }
  }, [error, lltsBranchId, location, history]);

  // This component doesn't render anything
  return null;
};
